import type { CSSProperties, ReactNode } from 'react';
import { LazyMotion, MotionConfig, domAnimation, m, useReducedMotion } from 'framer-motion';
import { AmbientMode, normalizeAmbientMode } from '../pal/ambient';
import { AccessoryId, FACE_STATE_NAMES, FaceState } from '../pal/face';
import { buildCartoonShapes, type AmbientGlyph, type CartoonEye } from '../pal/face-cartoon';
import { MOTION_ATOMS } from '../pal/motion';
import { PalForm } from '../pal/types';

export interface PalFaceCartoonProps {
    state: FaceState;
    form: PalForm;
    marks?: number;
    accessory?: AccessoryId;
    scale?: number;
    className?: string;
    maxWidth?: number | string;
    style?: CSSProperties;
    ambient?: AmbientMode;
    active?: boolean;
    onTap?: () => void;
    onLongPress?: () => void;
    nowMs?: number;
}

export function PalFaceCartoon(props: PalFaceCartoonProps) {
    const {
        state,
        form,
        marks = 0,
        accessory = AccessoryId.None,
        scale,
        className,
        maxWidth = 480,
        style,
        ambient = AmbientMode.NONE,
        active = true,
        nowMs,
    } = props;
    void scale;

    const prefersReducedMotion = useReducedMotion() ?? false;
    const motionEnabled = active !== false && !prefersReducedMotion;
    const motionState = motionEnabled ? 'active' : 'rest';
    const effectiveNowMs = motionEnabled ? (nowMs ?? now()) : 0;
    const safeAmbient = normalizeAmbientMode(ambient);
    const shapes = buildCartoonShapes({
        state,
        ambient: safeAmbient,
        form,
        marks,
        accessory,
        nowMs: effectiveNowMs,
    });

    const svg = (
        <m.svg
            role="img"
            aria-label={`Pal face ${FACE_STATE_NAMES[state]}`}
            data-testid="pal-face-cartoon"
            data-motion-state={motionState}
            viewBox={`0 0 ${shapes.width} ${shapes.height}`}
            initial="rest"
            animate={motionState}
            variants={MOTION_ATOMS.breathe}
            style={{ display: 'block', width: '100%', height: '100%' }}
        >
            <rect width={shapes.width} height={shapes.height} rx="16" fill={shapes.palette.background} />
            {shapes.aura && (
                <m.ellipse
                    cx="64"
                    cy="32"
                    rx="52"
                    ry="25"
                    fill={shapes.palette.eye}
                    opacity="0.28"
                    variants={MOTION_ATOMS.auraPulse}
                />
            )}
            <m.g variants={state === FaceState.INTENSE ? MOTION_ATOMS.shake : MOTION_ATOMS.look}>
                <Eye eye={shapes.leftEye} color={shapes.palette.pupil} />
                <Eye eye={shapes.rightEye} color={shapes.palette.pupil} />
            </m.g>
            {shapes.cheeks.map((cheek) => (
                <circle
                    key={`${cheek.cx}-${cheek.cy}`}
                    cx={cheek.cx}
                    cy={cheek.cy}
                    r={cheek.r}
                    fill={shapes.palette.cheek}
                    opacity={cheek.opacity}
                />
            ))}
            <m.path
                d={shapes.mouth.d}
                fill={shapes.mouth.open ? shapes.palette.accent : 'none'}
                stroke={shapes.palette.accent}
                strokeWidth={shapes.mouth.strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                variants={state === FaceState.DISCONNECTED ? MOTION_ATOMS.auraPulse : MOTION_ATOMS.squash}
            />
            <m.g variants={MOTION_ATOMS.particleFloat}>
                {[...shapes.glyphs, ...shapes.markGlyphs].map((glyph, index) => (
                    <Glyph key={`${glyph.kind}-${index}`} glyph={glyph} color={glyph.color ?? shapes.palette.accent} />
                ))}
            </m.g>
            <Accessory accessory={shapes.accessory} color={shapes.palette.accent} />
        </m.svg>
    );

    return (
        <div
            className={`rounded-2xl bg-black overflow-hidden ${className ?? ''}`}
            data-interactive={props.onTap || props.onLongPress ? 'true' : 'false'}
            style={{
                width: '100%',
                maxWidth,
                aspectRatio: '2 / 1',
                ...style,
            }}
        >
            <LazyMotion features={domAnimation}>
                {motionEnabled ? svg : <MotionConfig reducedMotion="always">{svg}</MotionConfig>}
            </LazyMotion>
        </div>
    );
}

function Eye({ eye, color }: { eye: CartoonEye; color: string }) {
    if (eye.style === 'closed') {
        return <path d={`M ${eye.cx - 9} ${eye.cy} Q ${eye.cx} ${eye.cy + 5} ${eye.cx + 9} ${eye.cy}`} stroke="#fff" strokeWidth="2.4" fill="none" strokeLinecap="round" />;
    }
    if (eye.style === 'x') {
        return (
            <g stroke="#fff" strokeWidth="3" strokeLinecap="round">
                <line x1={eye.cx - 7} y1={eye.cy - 7} x2={eye.cx + 7} y2={eye.cy + 7} />
                <line x1={eye.cx + 7} y1={eye.cy - 7} x2={eye.cx - 7} y2={eye.cy + 7} />
            </g>
        );
    }
    if (eye.style === 'spiral') {
        return <path d={spiralPath(eye.cx, eye.cy)} stroke="#fff" strokeWidth="2.2" fill="none" strokeLinecap="round" />;
    }
    if (eye.style === 'dollar') {
        return <text x={eye.cx} y={eye.cy + 7} fill="#fff" fontSize="18" fontWeight="700" textAnchor="middle">$</text>;
    }
    if (eye.style === 'star') {
        return <polygon points={starPoints(eye.cx, eye.cy, 11, 5)} fill="#fff" />;
    }
    if (eye.style === 'happy') {
        return <path d={`M ${eye.cx - 9} ${eye.cy + 2} Q ${eye.cx} ${eye.cy - 8} ${eye.cx + 9} ${eye.cy + 2}`} stroke="#fff" strokeWidth="2.6" fill="none" strokeLinecap="round" />;
    }
    return (
        <g>
            <m.ellipse
                cx={eye.cx}
                cy={eye.cy}
                rx={eye.rx}
                ry={eye.ry}
                fill="#fff"
                variants={MOTION_ATOMS.blink}
                style={{ originX: `${eye.cx}px`, originY: `${eye.cy}px` }}
            />
            <circle cx={eye.pupilCx} cy={eye.pupilCy} r={eye.pupilR} fill={color} />
            <circle cx={eye.pupilCx - eye.pupilR * 0.35} cy={eye.pupilCy - eye.pupilR * 0.35} r={Math.max(1, eye.pupilR * 0.22)} fill="#fff" opacity="0.82" />
        </g>
    );
}

function Glyph({ glyph, color }: { glyph: AmbientGlyph; color: string }) {
    const transform = `translate(${glyph.x} ${glyph.y}) scale(${glyph.scale})`;
    const opacity = glyph.opacity;
    if (glyph.kind === 'note') {
        return (
            <g transform={transform} opacity={opacity} fill={color}>
                <circle cx="0" cy="5" r="2.2" />
                <rect x="2" y="-7" width="1.6" height="12" rx="0.8" />
                <path d="M 3 -7 Q 8 -6 9 -2" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
            </g>
        );
    }
    if (glyph.kind === 'heart') {
        return <path transform={transform} opacity={opacity} fill={color} d="M 0 5 C -8 -1 -5 -8 0 -5 C 5 -8 8 -1 0 5 Z" />;
    }
    if (glyph.kind === 'alert') {
        return (
            <g transform={transform} opacity={opacity} stroke={color} strokeWidth="2" strokeLinecap="round">
                <line x1="0" y1="-6" x2="0" y2="2" />
                <circle cx="0" cy="6" r="1.2" fill={color} stroke="none" />
            </g>
        );
    }
    if (glyph.kind === 'snow') {
        return (
            <g transform={transform} opacity={opacity} stroke={color} strokeWidth="1.4" strokeLinecap="round">
                <line x1="-5" y1="0" x2="5" y2="0" />
                <line x1="0" y1="-5" x2="0" y2="5" />
                <line x1="-3.5" y1="-3.5" x2="3.5" y2="3.5" />
                <line x1="-3.5" y1="3.5" x2="3.5" y2="-3.5" />
            </g>
        );
    }
    if (glyph.kind === 'sleep') {
        return <text transform={transform} opacity={opacity} fill={color} fontSize="10" fontWeight="700">Z</text>;
    }
    return <polygon transform={transform} opacity={opacity} points={starPoints(0, 0, 6, 2.4)} fill={color} />;
}

function Accessory({ accessory, color }: { accessory: AccessoryId; color: string }): ReactNode {
    switch (accessory) {
        case AccessoryId.FirstHelmet:
            return <path d="M 38 16 Q 64 1 90 16 L 88 22 L 40 22 Z" fill={color} opacity="0.86" />;
        case AccessoryId.Sunglasses:
            return (
                <g fill="#050505" stroke={color} strokeWidth="1.5">
                    <rect x="32" y="22" width="22" height="11" rx="4" />
                    <rect x="74" y="22" width="22" height="11" rx="4" />
                    <line x1="54" y1="27" x2="74" y2="27" />
                </g>
            );
        case AccessoryId.Crown:
            return <path d="M 42 18 L 48 7 L 57 17 L 64 5 L 71 17 L 80 7 L 86 18 Z" fill={color} opacity="0.9" />;
        case AccessoryId.Cape:
            return <path d="M 16 58 Q 64 48 112 58 L 118 64 L 10 64 Z" fill={color} opacity="0.42" />;
        case AccessoryId.None:
        default:
            return null;
    }
}

function spiralPath(cx: number, cy: number): string {
    const points: string[] = [];
    for (let i = 0; i < 18; i += 1) {
        const r = i * 0.45;
        const a = i * 0.72;
        points.push(`${cx + Math.cos(a) * r} ${cy + Math.sin(a) * r}`);
    }
    return `M ${points.join(' L ')}`;
}

function starPoints(cx: number, cy: number, outer: number, inner: number): string {
    const points: string[] = [];
    for (let i = 0; i < 10; i += 1) {
        const r = i % 2 === 0 ? outer : inner;
        const a = -Math.PI / 2 + i * Math.PI / 5;
        points.push(`${cx + Math.cos(a) * r},${cy + Math.sin(a) * r}`);
    }
    return points.join(' ');
}

function now(): number {
    if (typeof performance !== 'undefined') return performance.now();
    return Date.now();
}
